import { observer } from 'mobx-react-lite'
import { lazy, useReducer, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/Button'
import { StatePanel } from '@/components/ui/StatePanel'
import { navLabel, type NavItem } from '@/config/navigation'
import { useT } from '@/i18n'
import { isChunkLoadError, reloadForNewVersion } from '@/lib/chunkReload'
import { PageHelpProvider } from '@/features/help/PageHelp'
import { PlannedPage } from '@/features/planned/PlannedPage'
import { isClosedForBalance, isPermitted, isPathAllowed } from '@/layout/access'
import { Splash } from '@/layout/Splash'
import { useStores } from '@/stores'

export const RequireAuth = observer(function RequireAuth({ children }: { children: ReactNode }) {
  const { auth } = useStores()
  const location = useLocation()
  if (auth.status === 'booting') return <Splash />
  if (auth.status !== 'authenticated') {
    const next = location.pathname + location.search
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
  }
  return <>{children}</>
})

export const RedirectIfAuthenticated = observer(function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { auth, session } = useStores()
  const location = useLocation()
  if (auth.status === 'booting') return <Splash />
  if (auth.status === 'authenticated') {
    // `next` may be another user's last page (the previous sign-in's): follow it
    // only when this profile can open it, else start on the home page.
    const next = new URLSearchParams(location.search).get('next')
    const path = next && next.startsWith('/') ? next.split(/[?#]/)[0] : ''
    return <Navigate to={path && (path === '/' || isPathAllowed(path, session)) ? next! : '/'} replace />
  }
  return <>{children}</>
})

const lazyPages = new Map<string, LazyExoticComponent<ComponentType>>()

/**
 * Drops a page's cached lazy(). React memoises a rejected lazy() for good, so
 * without this one failed import would leave the page dead for the whole tab —
 * reopening it would replay the same error and never refetch the chunk.
 */
function forgetPage(id: string) {
  lazyPages.delete(id)
}

function pageFor(item: NavItem) {
  let page = lazyPages.get(item.id)
  if (!page && item.load) {
    const { id, load } = item
    page = lazy(() =>
      load().catch((error: unknown) => {
        if (!isChunkLoadError(error)) throw error
        // A deploy replaced the hashed chunk this tab still asks for: reload onto the
        // new build. When that reload is suppressed — by the loop guard, or because it
        // did not fix it — drop the cached rejection so the boundary can retry.
        if (reloadForNewVersion()) return new Promise<never>(() => {})
        forgetPage(id)
        throw error
      }),
    )
    lazyPages.set(item.id, page)
  }
  return page
}

/**
 * Rejects forbidden routes before the module bundle loads. An old link to a
 * page the profile cannot open renders a real Forbidden state, never a silent
 * redirect.
 */
export const RouteGate = observer(function RouteGate({ item }: { item: NavItem }) {
  return (
    <PageHelpProvider key={item.id} item={item}>
      <RoutePage item={item} />
    </PageHelpProvider>
  )
})

const RoutePage = observer(function RoutePage({ item }: { item: NavItem }) {
  const { session } = useStores()
  const t = useT()
  // Retry rebuilds the children (see onRetry). The boundary resets itself, but only
  // the parent can hand it a new lazy(), so the attempt is counted here.
  const [attempt, retryAttempt] = useReducer((count: number) => count + 1, 0)
  if (item.readiness === 'planned') return <PlannedPage item={item} />
  if (isClosedForBalance(item, session)) {
    return (
      <StatePanel
        kind="forbidden"
        title={t('errors.states.pageForbiddenTitle')}
        description={t('errors.states.pageClosedForBalance', { page: navLabel(t, item), company: session.activeBalanceGroup?.code ?? '' })}
        action={
          <Link to="/">
            <Button>{t('common.actions.goToDashboard')}</Button>
          </Link>
        }
      />
    )
  }
  if (!isPermitted(item, session)) {
    const requirement = item.platformAdmin ? 'is_platform_admin' : (item.capability ?? item.anyCapability?.join(', ') ?? '')
    return (
      <StatePanel
        kind="forbidden"
        title={t('errors.states.pageForbiddenTitle')}
        description={t('errors.states.pageForbiddenText', { page: navLabel(t, item), capability: requirement })}
        action={
          <Link to="/">
            <Button>{t('common.actions.goToDashboard')}</Button>
          </Link>
        }
      />
    )
  }
  const Page = pageFor(item)
  if (!Page) return null
  return (
    <ErrorBoundary
      key={attempt}
      onRetry={() => {
        forgetPage(item.id)
        retryAttempt()
      }}
    >
      <Page />
    </ErrorBoundary>
  )
})

export function NotFoundPage() {
  const t = useT()
  return (
    <StatePanel
      kind="not-found"
      title={t('errors.states.pageNotFoundTitle')}
      description={t('errors.states.pageNotFoundText')}
      action={
        <Link to="/">
          <Button>{t('common.actions.goToDashboard')}</Button>
        </Link>
      }
    />
  )
}
